import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gmailEmailsApi, gmailSettingsApi } from "@/services/gmailApi";
import type {
  ClassifyEmailsResponse,
  FetchEmailsRequest,
  InMemoryEmail,
} from "@/types/gmail";
import { useToast } from "@/hooks/use-toast";

const gmailEmailKeys = {
  all: ["gmail", "emails"] as const,
  lists: () => [...gmailEmailKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) => [...gmailEmailKeys.lists(), filters] as const,
  detail: (id: string) => [...gmailEmailKeys.all, "detail", id] as const,
};

const gmailSettingsKeys = {
  all: ["gmail", "settings"] as const,
};

// ==================== Email List ====================

export const useGmailEmails = (params?: {
  status?: string;
  limit?: number;
  offset?: number;
}) => {
  return useQuery({
    queryKey: gmailEmailKeys.list(params as Record<string, unknown>),
    queryFn: () => gmailEmailsApi.list(params),
  });
};

export const useGmailEmail = (id: string | undefined) => {
  return useQuery({
    queryKey: gmailEmailKeys.detail(id || ""),
    queryFn: () => gmailEmailsApi.getById(id!),
    enabled: !!id,
  });
};

// ==================== Fetch (returns in-memory emails) ====================

export const useFetchGmailEmails = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (params: FetchEmailsRequest) => gmailEmailsApi.fetchFromGmail(params),
    onSuccess: (data) => {
      // Invalidate the DB email list so existing emails (PENDING_REVIEW, CONVERTED) refresh
      queryClient.invalidateQueries({ queryKey: gmailEmailKeys.lists() });
      const newCount = data.newEmails.length;
      const existingCount = data.existingEmails.length;
      if (newCount === 0 && existingCount === 0) {
        toast({
          title: "No Emails Found",
          description: "No emails found in the selected date range.",
        });
      } else {
        toast({
          title: "Emails Fetched",
          description:
            `Fetched ${data.totalFetched} email(s) total. ` +
            (newCount > 0 ? `${newCount} new to classify. ` : "") +
            (existingCount > 0 ? `${existingCount} already processed.` : ""),
        });
      }
    },
    onError: () => {
      toast({
        title: "Fetch Failed",
        description: "Failed to fetch emails from Gmail. Please try again.",
        variant: "destructive",
      });
    },
  });
};

// ==================== Classify (raw in-memory emails) ====================

export const useClassifyEmails = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (emails: InMemoryEmail[]) => gmailEmailsApi.classifyBatch(emails),
    onSuccess: (data: ClassifyEmailsResponse) => {
      // Refresh the DB list — newly saved PENDING_REVIEW emails will appear
      queryClient.refetchQueries({ queryKey: gmailEmailKeys.lists() });
      if (data.saved > 0 || data.rejected > 0) {
        toast({
          title: "Classification Complete",
          description:
            `${data.saved} quotation request(s) saved for review. ` +
            (data.rejected > 0 ? `${data.rejected} non-quotation email(s) discarded.` : ""),
        });
      } else {
        toast({
          title: "Classification Complete",
          description: "No emails were classified.",
        });
      }
    },
    onError: () => {
      toast({
        title: "Classification Failed",
        description: "Failed to classify emails. Please try again.",
        variant: "destructive",
      });
    },
  });
};

// ==================== Convert / Reject / Delete / Auto-Reply ====================

export const useConvertEmailToQuotation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId, quotationId }: { emailId: string; quotationId: string }) =>
      gmailEmailsApi.convert(emailId, quotationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gmailEmailKeys.lists() });
    },
  });
};

export const useRejectEmail = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, reviewNotes }: { id: string; reviewNotes?: string }) =>
      gmailEmailsApi.reject(id, reviewNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gmailEmailKeys.lists() });
      toast({
        title: "Email Rejected",
        description: "The email has been marked as rejected.",
      });
    },
  });
};

export const useDeleteEmail = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => gmailEmailsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gmailEmailKeys.lists() });
      toast({ title: "Email Deleted", description: "The email has been removed from the list." });
    },
    onError: () => {
      toast({
        title: "Delete Failed",
        description: "Could not delete the email. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useApproveAndAutoReply = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ emailId, quotationId }: { emailId: string; quotationId: string }) =>
      gmailEmailsApi.approveAndReply(emailId, quotationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gmailEmailKeys.lists() });
      toast({
        title: "Auto-Reply Sent",
        description: "The quotation reply has been sent to the customer.",
      });
    },
    onError: () => {
      toast({
        title: "Reply Failed",
        description: "Failed to send auto-reply. Please check your SMTP settings and try again.",
        variant: "destructive",
      });
    },
  });
};

// ==================== Gmail Settings ====================

export const useGmailSettings = () => {
  return useQuery({
    queryKey: gmailSettingsKeys.all,
    queryFn: () => gmailSettingsApi.get(),
  });
};

export const useUpdateGmailSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: Parameters<typeof gmailSettingsApi.update>[0]) =>
      gmailSettingsApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gmailSettingsKeys.all });
      toast({
        title: "Settings Saved",
        description: "Gmail settings have been updated.",
      });
    },
    onError: () => {
      toast({
        title: "Save Failed",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    },
  });
};
